import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl, articleSlugs } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.04)]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10 flex items-center justify-between h-16 md:h-18">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">
            Kakobuy<span className="text-primary-500">Finds</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/"
            className="text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap"
          >
            Home
          </Link>
          <Link
            to="/articles/what-is-kakobuy-spreadsheet"
            className="text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap"
          >
            Guides
          </Link>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors whitespace-nowrap"
          >
            Kakobuy Sheet
          </a>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-1.5 bg-primary-500 text-background-50 px-5 py-2 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Buy It Now
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </span>
          </a>
        </div>

        <button
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-3 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm font-medium text-foreground-700 py-2.5 px-2 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
            onClick={() => setMobileOpen(false)}
          >
            <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
              <i className="ri-home-line text-sm"></i>
            </span>
            Home
          </Link>

          <button
            onClick={() => setGuidesOpen(!guidesOpen)}
            className="flex items-center justify-between w-full text-sm font-medium text-foreground-700 py-2.5 px-2 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-article-line text-sm"></i>
              </span>
              Guides
            </span>
            <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${guidesOpen ? 'rotate-180' : ''}`}>
              <i className="ri-arrow-down-s-line text-sm"></i>
            </span>
          </button>

          {guidesOpen && (
            <div className="pl-9 flex flex-col gap-0.5 pb-1">
              {articleSlugs.map((article) => (
                <Link
                  key={article.slug}
                  to={`/articles/${article.slug}`}
                  className="text-sm text-foreground-500 py-2 px-2 rounded-lg hover:bg-background-100 hover:text-foreground-700 transition-colors whitespace-nowrap"
                  onClick={() => { setMobileOpen(false); setGuidesOpen(false); }}
                >
                  {article.title.length > 40 ? article.title.slice(0, 40) + '...' : article.title}
                </Link>
              ))}
            </div>
          )}

          <button
            onClick={() => setCatsOpen(!catsOpen)}
            className="flex items-center justify-between w-full text-sm font-medium text-foreground-700 py-2.5 px-2 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-grid-line text-sm"></i>
              </span>
              Categories
            </span>
            <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${catsOpen ? 'rotate-180' : ''}`}>
              <i className="ri-arrow-down-s-line text-sm"></i>
            </span>
          </button>

          {catsOpen && (
            <div className="grid grid-cols-2 gap-1 pl-9 pr-2 pb-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-1.5 text-xs text-foreground-500 py-2 px-1.5 rounded-lg hover:bg-background-100 hover:text-foreground-700 transition-colors whitespace-nowrap truncate"
                  onClick={() => setMobileOpen(false)}
                >
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  <span className="truncate">{cat.name}</span>
                </a>
              ))}
            </div>
          )}

          <div className="border-t border-background-200 my-1"></div>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-2 text-sm font-medium text-foreground-700 py-2.5 px-2 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
            onClick={() => setMobileOpen(false)}
          >
            <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
              <i className="ri-external-link-line text-sm"></i>
            </span>
            Kakobuy Sheet
          </a>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center justify-center gap-1.5 bg-primary-500 text-background-50 px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer mt-1"
            onClick={() => setMobileOpen(false)}
          >
            Buy It Now
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </span>
          </a>
        </div>
      )}
    </nav>
  );
}